import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';
import { SelectableList } from 'components/UI';
import i18n from 'i18n-js';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { uiActions } from 'state/modules/UI';

import useDeepCompareEffect from 'use-deep-compare-effect';

const filteredLocations = (locations = [], assignedLocations = []) => {
  const filteredArray = locations.map((location) => {
    if (assignedLocations?.length === 0) {
      location.disabled = false;
      location.selected = false;
      return location;
    } else {
      for (let i = 0; i < assignedLocations.length; i++) {
        if (assignedLocations[i]?.id === location.id) {
          location.disabled = true;
        }
      }
      return location;
    }
  });
  return filteredArray;
};

const locationsColumns = [
  {
    name: 'Zone',
    selector: 'name',
    sortable: false,
  },
];

const AvailablesLocations = ({
  data = [],
  handlePageChange,
  handlePerRowsChange,
  loading,
  onRowClicked,
  totalRows = 0,
  paginationDefaultPage,
  paginationPerPage,
}) => {
  return (
    <SelectableList
      mainTable={true}
      dataCy="available-list"
      columns={locationsColumns}
      data={data}
      loading={loading}
      handlePageChange={handlePageChange}
      onRowClicked={onRowClicked}
      handlePerRowsChange={handlePerRowsChange}
      title={i18n.t('vehicle_groups.available_locations')}
      totalRows={totalRows}
      paginationDefaultPage={paginationDefaultPage}
      paginationPerPage={paginationPerPage}
    />
  );
};

AvailablesLocations.propTypes = {
  data: PropTypes.array,
  handlePageChange: PropTypes.func,
  handlePerRowsChange: PropTypes.func,
  loading: PropTypes.bool,
  onRowClicked: PropTypes.func,
  totalRows: PropTypes.number,
  paginationDefaultPage: PropTypes.number,
  paginationPerPage: PropTypes.number,
};

const assignedColumns = [
  {
    name: 'Zone',
    selector: 'name',
    sortable: false,
  },
];

const SelectedLocations = ({
  data = [],
  loading = false,
  onRowClicked,
  handlePageChange,
  handlePerRowsChange,
  totalRows,
  paginationDefaultPage,
  paginationPerPage,
}) => {
  return (
    <SelectableList
      dataCy="selected-list"
      columns={assignedColumns}
      data={data}
      loading={loading}
      onRowClicked={onRowClicked}
      handlePageChange={handlePageChange}
      handlePerRowsChange={handlePerRowsChange}
      totalRows={totalRows}
      paginationDefaultPage={paginationDefaultPage}
      title={i18n.t('vehicle_groups.selected_locations')}
      paginationPerPage={paginationPerPage}
    />
  );
};

SelectedLocations.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  onRowClicked: PropTypes.func,
  handlePageChange: PropTypes.func,
  handlePerRowsChange: PropTypes.func,
  totalRows: PropTypes.number,
  paginationDefaultPage: PropTypes.number,
  paginationPerPage: PropTypes.number,
};

const VehiclesGroupsLocations = ({
  currentGroup,
  step = {},
  loading,
  type,
  parentGroup,
  showAvailableZoneText = true,
}) => {
  const dispatch = useDispatch();
  const [availableParams, setAvailableParams] = useState(() => {
    const page = 1;
    const limit = 10;
    return { page, limit };
  });
  const [assignedParams, setAssignedParams] = useState(() => {
    const page = 1;
    const limit = 10;
    return { page, limit };
  });
  const [assignedLocations, setAssignedLocations] = useState([]);
  const [availablesLocations, setAvailablesLocations] = useState([]);
  const [selectedAssignedRow, setSelectedAssignedRow] = useState(null);
  const [selectedLocationRow, setSelectedAvailablesRow] = useState(null);

  const addLocationToVehicleGroup = () => {
    if (!selectedLocationRow?.id) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle_groups.add_location_message'),
        })
      );
    }
    const payload = {
      location_type: type === 'service' ? 'MANAGED' : 'PRIMARY',
      location_id: selectedLocationRow?.id,
    };

    dispatch(VehiclesGroupsActions.fetchAddLocationToVehiclesGroups(currentGroup.id, payload));
  };

  const removeLocationFromVehicleGroup = () => {
    if (!selectedAssignedRow?.id) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle_groups.remove_location_message'),
        })
      );
    }

    dispatch(
      VehiclesGroupsActions.fetchRemoveLocationFromVehiclesGroups(
        currentGroup.id,
        selectedAssignedRow.id
      )
    );
  };

  const fetchLocations = (params = {}) => {
    dispatch(VehiclesGroupsActions.fetchAvailableLocations(params));
  };

  const fetchVehiclesGroupsLocations = (id, type, params) => {
    dispatch(VehiclesGroupsActions.fetchVehiclesGroupsLocations(id, 'locations', type, params));
  };

  useDeepCompareEffect(() => {
    if (currentGroup?.id) {
      fetchVehiclesGroupsLocations(currentGroup.id, 'assigned', assignedParams);
    }
  }, [currentGroup, assignedParams]);

  useDeepCompareEffect(() => {
    if (parentGroup) {
      fetchVehiclesGroupsLocations(parentGroup, 'availables', availableParams);
    }

    if (type === 'service') {
      fetchLocations(availableParams);
    }
  }, [availableParams]);

  useDeepCompareEffect(() => {
    if (step?.assigned?.list) {
      setAssignedLocations(step.assigned.list || []);
    }

    if (step?.availables?.list) {
      setAvailablesLocations(filteredLocations(step?.availables?.list, step?.assigned?.list) || []);
    }

    setSelectedAssignedRow(null);
    setSelectedAvailablesRow(null);
  }, [step]);

  const handleAvailablePageChange = (page) =>
    setAvailableParams({ page, limit: availableParams.limit });
  const handleAssignedPageChange = (page) =>
    setAssignedParams({ page, limit: assignedParams.limit });

  const handleAvailablePerRowsChange = (limit) => setAvailableParams({ page: 1, limit });
  const handleAssignedPerRowsChange = (limit) => setAssignedParams({ page: 1, limit });

  return (
    <Box>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={12}>
            <Typography gutterBottom component="div">
              <Box fontWeight="fontWeightBold" data-cy="step-title" fontSize={14} my={2}>
                {showAvailableZoneText && i18n.t('vehicle_groups.select_available_zone')}
              </Box>
            </Typography>
            <Typography gutterBottom component="div">
              <Box fontSize={14} mb={4}>
                {i18n.t('vehicle_groups.location_description_fleet')}
              </Box>
            </Typography>
          </Grid>
          <Grid size={12}>
            <Grid container direction="row" justifyContent="space-around" alignItems="flex-start">
              <Grid size={5}>
                <AvailablesLocations
                  data={availablesLocations}
                  handlePageChange={handleAvailablePageChange}
                  handlePerRowsChange={handleAvailablePerRowsChange}
                  loading={step?.availables?.loading}
                  onRowClicked={setSelectedAvailablesRow}
                  totalRows={step?.availables?.pagination?.totalRows}
                  paginationDefaultPage={availableParams.page}
                  paginationPerPage={availableParams.limit}
                />
              </Grid>
              <Grid size={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Box py={1} px={2} style={{ width: '100%' }}>
                    <Button
                      disabled={loading || !selectedLocationRow}
                      onClick={addLocationToVehicleGroup}
                      sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                      data-cy="add-location"
                      variant="outlined"
                      color="primary"
                      fullWidth
                    >
                      {i18n.t('vehicle_groups.add')}
                      <ArrowForwardIcon fontSize="inherit" />
                    </Button>
                  </Box>
                  {assignedLocations?.length > 0 && !step?.assigned?.loading && (
                    <Box py={1} px={2} style={{ width: '100%' }}>
                      <Button
                        disabled={loading || !selectedAssignedRow}
                        onClick={removeLocationFromVehicleGroup}
                        data-cy="remove-location"
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                        variant="outlined"
                        color="primary"
                        fullWidth
                      >
                        <ArrowBackIcon fontSize="inherit" />
                        {i18n.t('vehicle_groups.delete')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid size={5}>
                <Box mb={5}>
                  <SelectedLocations
                    data={assignedLocations}
                    loading={step?.assigned?.loading}
                    onRowClicked={setSelectedAssignedRow}
                    handlePageChange={handleAssignedPageChange}
                    handlePerRowsChange={handleAssignedPerRowsChange}
                    totalRows={step?.assigned?.pagination?.totalRows}
                    paginationDefaultPage={assignedParams.page}
                    paginationPerPage={assignedParams.limit}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

VehiclesGroupsLocations.propTypes = {
  currentGroup: PropTypes.object,
  step: PropTypes.object,
  loading: PropTypes.bool,
  type: PropTypes.string,
  parentGroup: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showAvailableZoneText: PropTypes.bool,
};

export default VehiclesGroupsLocations;
